import { Button, Form, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import 'bootstrap-icons/font/bootstrap-icons.css';
import { TemplateFieldDto, ErrorTemplateFieldDto } from "../types";
import { ExtraFieldObjectTypeEnum } from "../../surveyImportExtraFields/types/enums";
import { SurveyImportExtraFieldFormDto } from "../../surveyImportExtraFields/types";
import { SelectItemDto } from "../../../../types/SelectItemDto";

type TemplateFieldProps = {
    indexField: number;
    currentField: TemplateFieldDto;
    fields: TemplateFieldDto[];
    extraFieldOptions: SurveyImportExtraFieldFormDto[];
    orderOptions: SelectItemDto[];
    setFields: (fields: TemplateFieldDto[]) => void;
    showPlus: boolean;
    errorsField?: ErrorTemplateFieldDto
};

export default function TemplateField({ indexField, currentField, fields, extraFieldOptions, orderOptions, setFields, errorsField }: TemplateFieldProps) {
    const { t } = useTranslation();
    
    const selectedExtraFieldOption = extraFieldOptions.find(x => x.id == currentField.extraFieldId);
    let fieldsDisabled = !selectedExtraFieldOption || !!selectedExtraFieldOption.description;

    const isShowInHistoricalFieldDisabled = (): boolean => {
        if(!selectedExtraFieldOption)
            return true;

        const numberOfFieldInHistorical = fields.filter(x => 
                    x.showInHistorical 
                    && x.objectType != ExtraFieldObjectTypeEnum.TextForEvaluateAI
                    && extraFieldOptions.find(option => option.id == x.extraFieldId)?.fieldName != 'AnswerDate'
                ).length;

        const isHistoryDisabled: boolean = 
            selectedExtraFieldOption.objectType == ExtraFieldObjectTypeEnum.TextForEvaluateAI
            || selectedExtraFieldOption.fieldName == 'AnswerDate'
            || (numberOfFieldInHistorical >= 6 && !currentField.showInHistorical)
        return isHistoryDisabled;
    }

    const onFieldChange = (fieldName: keyof TemplateFieldDto, value: TemplateFieldDto[keyof TemplateFieldDto]) => {
        const newFields = [...fields]; 
        newFields[indexField] = {
            ...newFields[indexField],
            [fieldName]: value, 
        };
        setFields(newFields);
    }
    
    const handleChangeextraFieldId = (selectedExtraField: SurveyImportExtraFieldFormDto) => {
        const newFields = [...fields];
        newFields[indexField] = {
            ...newFields[indexField],
            extraFieldId: selectedExtraField.id,
            description: selectedExtraField.description ?? currentField.description,
            objectType: selectedExtraField.objectType ?? currentField.objectType,
            isFilterable: selectedExtraField.isFilterable ?? currentField.isFilterable,
            showInHistorical: selectedExtraField.showInHistorical ?? currentField.showInHistorical,
            historicalColumnName: selectedExtraField.historicalColumnName ?? currentField.historicalColumnName,
            orderInHistorical: selectedExtraField.orderInHistorical ?? currentField.orderInHistorical
        };
        setFields(newFields);
    };

    const handleDeleteField = () => {
        const updatedFields = fields.filter((_, index) => index !== indexField);
        setFields([...updatedFields]);
    };
   
    const getObjectTypeOptions = () => {
        if(!selectedExtraFieldOption)
            return;

        if (selectedExtraFieldOption.objectType == ExtraFieldObjectTypeEnum.NPS) {
            return (
                <option key={0} value={ExtraFieldObjectTypeEnum.NPS}>
                    {t(`extraField:objectType.${ExtraFieldObjectTypeEnum[ExtraFieldObjectTypeEnum.NPS]}`)}
                </option>
            );
        }
        if (selectedExtraFieldOption.objectType == ExtraFieldObjectTypeEnum.TextForEvaluateAI) {
            return (
                <option key={0} value={ExtraFieldObjectTypeEnum.TextForEvaluateAI}>
                    {t(`extraField:objectType.${ExtraFieldObjectTypeEnum[ExtraFieldObjectTypeEnum.TextForEvaluateAI]}`)}
                </option>
            );
        }
        if (selectedExtraFieldOption.objectType == ExtraFieldObjectTypeEnum.Campaign) {
            return (
                <option key={0} value={ExtraFieldObjectTypeEnum.Campaign}>
                    {t(`extraField:objectType.${ExtraFieldObjectTypeEnum[ExtraFieldObjectTypeEnum.Campaign]}`)}
                </option>
            );
        }
        if (selectedExtraFieldOption.objectType == ExtraFieldObjectTypeEnum.CampaignDescription) {
            return (
                <option key={0} value={ExtraFieldObjectTypeEnum.CampaignDescription}>
                    {t(`extraField:objectType.${ExtraFieldObjectTypeEnum[ExtraFieldObjectTypeEnum.CampaignDescription]}`)}
                </option>
            );
        }
        if (selectedExtraFieldOption.fieldName == "AnswerDate" || selectedExtraFieldOption.fieldName == "FieldCreateDate") {
            return (
                <option key={0} value={ExtraFieldObjectTypeEnum.InfoDate}>
                    {t(`extraField:objectType.${ExtraFieldObjectTypeEnum[ExtraFieldObjectTypeEnum.InfoDate]}`)}
                </option>
            );
        }
        else {
            return(<>
                <option key={0} value={ExtraFieldObjectTypeEnum.Info}>
                    {t(`extraField:objectType.${ExtraFieldObjectTypeEnum[ExtraFieldObjectTypeEnum.Info]}`)}
                </option>
                <option key={1} value={ExtraFieldObjectTypeEnum.InfoDate}>
                    {t(`extraField:objectType.${ExtraFieldObjectTypeEnum[ExtraFieldObjectTypeEnum.InfoDate]}`)}
                </option>
            </>);
        }
    }

    return (
        <div className="card p-4 pt-3 m-2">
            <Row>
                <Col xs={12} className="d-flex justify-content-end">
                    <Button onClick={handleDeleteField} variant="gris2" style={{ width: "32px", height: "32px", display: "flex", justifyContent: "center" }}>
                        <i className="bi bi-x-lg" style={{ fontSize: "12px" }}></i>
                    </Button>
                </Col>
            </Row>
            <Row>
                <Col xs={4}>
                    <Form.Label>{t("template:duplicate.newTemplateFieldOrder")}</Form.Label>
                    <Form.Control
                        type="number"
                        className="form-control"
                        value={currentField?.order ?? ""}
                        min="0"
                        isInvalid={!!errorsField?.order}
                        onChange={(e) => onFieldChange('order', Number(e.target.value))}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errorsField?.order}
                    </Form.Control.Feedback>
                </Col>
                <Col xs={4}>
                    <Form.Label>{t("template:duplicate.newTemplateFieldOriginFieldName")}</Form.Label>
                    <Form.Control
                        type="text"
                        className="form-control"
                        value={currentField?.originFieldName}
                        isInvalid={!!errorsField?.originFieldName}
                        onChange={(e) => onFieldChange('originFieldName', e.target.value)}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errorsField?.originFieldName}
                    </Form.Control.Feedback>
                </Col>
                <Col xs={4}>
                    <Form.Label>{t("template:duplicate.newTemplateFieldObject")}</Form.Label>
                    <Form.Control
                        as="select"
                        className={`form-select`}
                        value={currentField?.objectType}
                        onChange={(e) => onFieldChange('objectType', Number(e.target.value))}
                        isInvalid={!!errorsField?.objectType}
                        disabled={fieldsDisabled}
                    >
                        {getObjectTypeOptions()}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">
                        {errorsField?.objectType}
                    </Form.Control.Feedback>
                </Col>
            </Row>
            <Row>
                <Col xs={4}>
                    <Form.Label>{t("template:duplicate.newTemplateFieldDestinationFieldName")}</Form.Label>
                    <Form.Control
                        as="select"
                        className="form-select"
                        value={currentField?.extraFieldId}
                        isInvalid={!!errorsField?.extraFieldId}
                        onChange={(e) => {
                            const selectedValue = Number(e.target.value);
                            if (selectedValue !== -1) {
                                handleChangeextraFieldId(extraFieldOptions.find(x => x.id == selectedValue)!);
                            }
                        }}
                    >
                        <option value="-1" disabled hidden></option>
                        {extraFieldOptions
                            .filter(option => option.id == selectedExtraFieldOption?.id || !fields.some(field => field.extraFieldId == option.id))
                            .map((option, index) => (
                                <option key={index} value={option.id}>
                                    {`${(option.description ? `${option.description} - ` : "")} ${t(`extraField:field.${option.fieldName}`)}`}
                                </option>
                            ))
                        }
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">
                        {errorsField?.extraFieldId}
                    </Form.Control.Feedback>
                </Col>                
                <Col xs={4}>
                    <Form.Label>{t("template:duplicate.newTemplateFieldDescriptionFieldName")}</Form.Label>
                    <Form.Control
                        type="text"
                        className="form-control"
                        value={currentField?.description}
                        isInvalid={!!errorsField?.description}
                        disabled={fieldsDisabled}
                        onChange={(e) => onFieldChange('description', e.target.value)}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errorsField?.description}
                    </Form.Control.Feedback>
                </Col>
                <Col xs={4}>
                    <Form.Label>{t("template:duplicate.newTemplateFieldFilter")}</Form.Label>
                    <Form.Control
                        as="select"
                        className={`form-select`}
                        value={String(currentField.isFilterable)}
                        onChange={(e) => onFieldChange('isFilterable', e.target.value === "true")}
                        disabled={currentField.objectType == ExtraFieldObjectTypeEnum.TextForEvaluateAI || fieldsDisabled}
                    >
                        <option value="true">{t("common:yes")}</option>
                        <option value="false">{t("common:no")}</option>
                    </Form.Control>
                </Col>
            </Row>
            <Row>       
                <Col xs={4}>
                    <Form.Label>{t("template:duplicate.isHistory")}</Form.Label>
                    <Form.Control
                        as="select"
                        className={`form-select`}
                        value={String(currentField.showInHistorical)}
                        onChange={(e) => onFieldChange('showInHistorical', e.target.value === "true")}
                        disabled={isShowInHistoricalFieldDisabled() || fieldsDisabled}
                    >
                        <option value="true">{t("common:yes")}</option>
                        <option value="false">{t("common:no")}</option>
                    </Form.Control>
                </Col>    
                <Col xs={4}>
                    <Form.Label>{t("template:duplicate.columnName")}</Form.Label>
                    <Form.Control
                        type="text"
                        className="form-control"
                        value={currentField.showInHistorical ? currentField?.historicalColumnName : ""}
                        isInvalid={!!errorsField?.historicalColumnName}
                        disabled={fieldsDisabled || !currentField.showInHistorical}
                        onChange={(e) => onFieldChange('historicalColumnName', e.target.value)}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errorsField?.historicalColumnName}
                    </Form.Control.Feedback>
                </Col>
                <Col xs={4}>
                    <Form.Group>
                        <Form.Label>{t("extraField:form.fields.orderInHistorical")}</Form.Label>                                    
                        <Form.Select
                            value={currentField.orderInHistorical ?? ""}
                            isInvalid={!!errorsField?.orderInHistorical}
                            disabled={fieldsDisabled || !currentField.showInHistorical}
                            onChange={(e) => {
                                onFieldChange('orderInHistorical', Number(e.target.value));
                            }}
                        >
                            <option key={-1} value={""}></option>
                            {orderOptions.map((option) => (
                                <option key={option.id} value={option.id}>{option.name}</option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errorsField?.orderInHistorical}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col> 
            </Row>
        </div>
    );
}
